/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import * as React from 'react';
import classNames from 'classnames';

export interface TooltipProps extends React.HTMLAttributes<HTMLSpanElement> {
    variant?: 'success' | 'warning' | 'error';
    tooltipWidth?: 'dynamic' | 'fixed';
    content?: React.ReactNode;
}

/**
 * @name      a-tooltip
 * @type      atom
 * @copyright Robert Bosch GmbH
 *
 * @param   {content} string   Array of content which should be rendered
 * @param   {tooltipWidth} string            Type of Width definition (Default, Dynamic or Fixed)
 * @param   {variant} string      Type of Tooltip definition (Neutral, Success, Error or Warning)
 * @description
 * representation of tooltips
 */
export const Tooltip = React.forwardRef<HTMLElement, TooltipProps>(
    ({ tooltipWidth, variant, content, children, ...rest }, ref) => {
        const tooltipDataAttributes = {
            'data-tooltip': typeof content === 'string' ? content : undefined,
            'data-tooltip-width': tooltipWidth,
            'data-tooltip-type': variant,
        };
        if (typeof children === 'string') {
            // with single child of type string
            return (
                <span
                    {...rest}
                    role="tooltip"
                    {...tooltipDataAttributes}
                    ref={ref}
                >
                    {children}
                </span>
            );
        } else if (React.isValidElement(children)) {
            // with any children
            return (
                <>
                    {React.Children.map(children, child => {
                        return React.cloneElement(child, {
                            ...tooltipDataAttributes,
                            ref,
                        });
                    })}
                </>
            );
        } else {
            // no children at all
            const tooltipClass = classNames('a-tooltip', {
                [`a-tooltip--${variant}`]: variant,
                [`-${tooltipWidth}-width`]: tooltipWidth,
            });
            return (
                <span
                    role="tooltip"
                    className={tooltipClass}
                    {...rest}
                    ref={ref}
                >
                    {content}
                </span>
            );
        }
    },
);
